import { FC, useEffect, useState } from 'react';
import { Avatar, Button, Dropdown, Menu, Space } from 'antd';
import { TbCopyPlus, TbDotsVertical, TbEdit, TbTrash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownMenuPanel, VirtualizedList as List } from '@/shared';
import { DeletePrimitiveModal } from '@/shared/components/modals/ResourceActionModals';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useMemberStore } from '@/stores';
import { useRoleStore } from '@/stores/database/entities/00-RBAC/04-Roles';

import type { RoleListEntry } from '..';
import ViewRolePermissionsModal from './components/ViewRolePermissionsModal';

interface RoleListItemProps {
    role: RoleListEntry;
}

const RoleListItem: FC<RoleListItemProps> = ({ role }): JSX.Element => {
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [duplicating, setDuplicating] = useState(false);

    const { token } = useAntDesignTheme();
    const { createRole, deleteRole } = useRoleStore();
    const { fetchMembers, members } = useMemberStore();

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const memberCount = role.type ? members.filter((member) => member.permission === role.type).length : undefined;
    const policyCount = role.role?.permissions?.length ?? 0;

    const handleDeleteRole = async () => {
        if (role.system || !role.role) {
            return;
        }

        try {
            await deleteRole(role.role.id);

            toast('Role Deleted', {
                icon: <TbTrash size='1rem' />,
                description: role.label,
            });
        } catch (error) {
            toast.error('Unable to delete role');
            throw error;
        }
    };

    const handleDuplicateRole = async () => {
        if (role.system || !role.role) {
            return;
        }

        try {
            setDuplicating(true);
            const duplicatedRole = await createRole({
                name: `Copy of ${role.label}`,
                permissions: role.role.permissions.map(({ action, constraints, scope, subject }) => ({
                    action,
                    constraints,
                    scope,
                    subject,
                })),
            });

            if (!duplicatedRole) {
                throw new Error('Role duplication failed');
            }

            toast('Role Duplicated', {
                icon: <TbCopyPlus size='1rem' />,
                description: duplicatedRole.name,
            });
        } catch {
            toast.error('Unable to Duplicate Role');
        } finally {
            setDuplicating(false);
        }
    };

    return (
        <List.Item key={role.key} style={{ paddingLeft: 0, paddingRight: 0 }}>
            <List.Item.Meta
                title={
                    <>
                        <Avatar
                            size={16}
                            shape='square'
                            style={{
                                marginRight: '0.375rem',
                                verticalAlign: '-0.5px',
                                border: `1px solid ${token.colorBorder}`,
                            }}
                            icon={role.icon}
                        />
                        {role.label}
                    </>
                }
                description={
                    <small>
                        {role.system
                            ? `${memberCount ?? 0} Workspace ${(memberCount ?? 0) === 1 ? 'Member' : 'Members'}`
                            : `${policyCount} Custom ${policyCount === 1 ? 'Policy' : 'Policies'}`}
                    </small>
                }
            />
            <Space.Compact size='small'>
                <Button size='small' onClick={() => setOpen(true)}>
                    View Permissions
                </Button>
                {!role.system && (
                    <Dropdown
                        trigger={['click']}
                        placement='bottomRight'
                        popupRender={() => (
                            <DropdownMenuPanel style={{ marginTop: '0.5rem' }}>
                                <Menu.Item icon={<TbEdit size='1rem' />} onClick={() => setEditOpen(true)}>
                                    Edit Role
                                </Menu.Item>
                                <Menu.Item
                                    disabled={duplicating}
                                    icon={<TbCopyPlus size='1rem' />}
                                    onClick={() => void handleDuplicateRole()}
                                >
                                    Duplicate Role
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item danger icon={<TbTrash size='1rem' />} onClick={() => setDeleteOpen(true)}>
                                    Delete Role
                                </Menu.Item>
                            </DropdownMenuPanel>
                        )}
                    >
                        <Button size='small'>
                            <TbDotsVertical style={{ color: token.colorTextTertiary }} />
                        </Button>
                    </Dropdown>
                )}
            </Space.Compact>
            <ViewRolePermissionsModal role={role} open={open} setOpen={setOpen} />
            {!role.system && <ViewRolePermissionsModal mode='edit' role={role} open={editOpen} setOpen={setEditOpen} />}
            {!role.system && (
                <DeletePrimitiveModal
                    open={deleteOpen}
                    resourceLabel='Role'
                    resourceName={role.label}
                    warning='Deleting this role removes it from the workspace and can change access for assigned members.'
                    onCancel={() => setDeleteOpen(false)}
                    onDelete={handleDeleteRole}
                />
            )}
        </List.Item>
    );
};

export default RoleListItem;
